import { formatDateShortLocale } from '@qsrsoft/myq-core';
import { columnCurrency, columnText } from '@/components/gridContainer';
import { postEventDetailsBatched } from './securityService';

const baseURL = 'security/event_details/v1';

/**
 * Get occurence detail
 * @param {Object} params
 * @param {string} params.nsn
 * @param {string} params.orgId
 * @param {string} params.event_token
 * @param {string} params.start_date
 * @param {string} params.end_date
 * @param {string[]} params.registers
 * @param {string[]} params.time_slices
 * @param {string[]} params.cashiers
 * @param {string[]} params.managers
 * @param {string} params.mgr_code
 * @param {string[]} params.tender_types
 * @param {string} params.lock_out_type
 * @param {Object} params.extraParams
 * @returns {Promise}
 */
export async function getOccurenceDetail(params) {
  if (!params.nsn) throw new Error('params.nsn is required');
  if (!params.orgId) throw new Error('params.orgId is required');
  const url = `${baseURL}/${params.orgId}/${params.nsn}`;
  const resp = await postEventDetailsBatched(url, params, {
    orgId: params.orgId,
  });
  return {
    resp,
    columns: getColumns(params),
  };
}

/**
 * Get columns
 * @param {Object} event
 * @returns {Array}
*/
function getColumns(event) {
  if (!event?.event_token) return [];
  const eventAmt = columnCurrency({
    field: 'event_amt',
    headerName: eventHeaderNames[event.event_token] || 'Amount',
    minWidth: 90,
  });
  const fields = eventColumnsMap[event.event_token] || eventColumnsMap.default;
  // const fields = ['date', 'time', 'details', 'camera'];
  const columns = fields.map((field) => {
    if (field === 'eventAmt') return eventAmt;
    return allColumns[field];
  });
  return columns;
}

const allColumns = {
  date: columnText({
    field: 'store_busn_dt',
    headerName: 'Date',
    pinned: 'left',
    cellClass: 'date',
    valueFormatter: ({ value }) => {
      if (!value) return '';
      return formatDateShortLocale(value, 'YYYY-MM-DD');
    },
  }),
  time: columnText({
    field: 'event_tm',
    headerName: 'Time',
    pinned: 'left',
  }),
  dayPart: columnText({ field: 'daypart_name', headerName: 'Day Part' }),
  register: columnText({
    field: 'reg_num',
    headerName: 'Register',
    valueFormatter: ({ value, node }) => {
      if (node.rowPinned) return '';
      return value;
    } 
  }),
  crew: columnText({ field: 'crew', headerName: 'Crew' }),
  manager: columnText({ field: 'mgr', headerName: 'Manager' }),
  mgr_code: columnText({
    field: 'mgr_code',
    headerName: 'Manager Code Entered',
    valueGetter: ({ data, node }) => {
      if (node?.rowPinned) return '';
      return data?.mgr_code === 'true';
    },
    // cellRenderer: 'agCheckboxCellRenderer',
    cellRendererSelector: ({ node }) => !node.rowPinned ? { component: 'agCheckboxCellRenderer' } : null,
  }),
  details: columnText({
    field: 'actions',
    headerName: 'View Details',
    cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
    filter: false,
  }),
  camera: columnText({
    field: 'camera',
    headerName: 'Camera',
    cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
    filter: false,
  }),
  remainingAmt: columnCurrency({
    field: 'remaining_amt',
    headerName: 'Remaining Amount',
  }),
  tenderType: columnText({ field: 'tender_type', headerName: 'Tender Type' }),
  loyaltyId: columnText({ field: 'loyalty_id', headerName: 'Loyalty ID' }),
  eventType: columnText({ field: 'event_type', headerName: 'Type' }),
  promoType: columnText({ field: 'event_display', headerName: 'Promo Type' }),
  discountType: columnText({ field: 'event_display', headerName: 'Discount Type' }),
  lock_out_type: columnText({ field: 'event_display', headerName: 'Lock Out Type' }),
  item_count: columnText({ field: 'item_count', headerName: 'Item Count' }),
  couponType: columnText({ field: 'event_display', headerName: 'Coupon Type' }),
}

const eventHeaderNames = {
  t_red_before: 'Reduction Amount',
  t_red_after: 'Reduction Amount',
  cash_refund: 'Refund Amount',
  cashless_refund: 'Refund Amount',
  pos_overring: 'Overring Amount',
  pos_auto_discount: 'Discount Amount',
  mobile_discount: 'Discount Amount',
  other_discount: 'Discount Amount',
  mobile_promo: 'Promo Amount',
  other_promo: 'Promo Amount',
  total_order_promo: 'Promo Amount',
  loyalty_reward_ids: 'Amount',
  employee_meal: 'Amount',
  manager_meal: 'Amount',
  duplicate_card_swipe: 'Amount',
  coupon: 'Discount Amount',
  all_discount: 'Discount Amount',
  all_promo: 'Promo Amount',
  billable_sales: 'Amount',
  high_lock_out: 'Amount',
  delivery_promo: 'Promo Amount',
  electronic_benefit_transfer: 'Amount',
};

const eventColumnsMap = {
  t_red_before: [
    'date',
    'time',
    'dayPart',
    'register',
    'crew',
    'manager',
    'mgr_code',
    'eventAmt',
    'remainingAmt',
    'details',
    'camera',
  ],
  t_red_after: [
    'date',
    'time',
    'dayPart',
    'register',
    'crew',
    'manager',
    'mgr_code',
    'eventAmt',
    'remainingAmt',
    'details',
    'camera',
  ],
  mobile_promo: [
    'date',
    'time',
    'dayPart',
    'register',
    'crew',
    'manager',
    'mgr_code',
    'tenderType',
    'eventAmt',
    'remainingAmt',
    'details',
    'camera',
  ],
  mobile_discount: [
    'date',
    'time',
    'dayPart',
    'register',
    'crew',
    'manager',
    'mgr_code',
    'tenderType',
    'eventAmt',
    'remainingAmt',
    'details',
    'camera',
  ],
  other_promo: [
    'date',
    'time',
    'dayPart',
    'register',
    'crew',
    'manager',
    'mgr_code',
    'tenderType',
    'eventAmt',
    'remainingAmt',
    'details',
    'camera',
  ],
  total_order_promo: [
    'date',
    'time',
    'dayPart',
    'register',
    'crew',
    'manager',
    'mgr_code',
    'eventAmt',
    'remainingAmt',
    'details',
    'camera',
  ],
  pos_auto_discount: [
    'date',
    'time',
    'dayPart',
    'register',
    'crew',
    'manager',
    'mgr_code',
    'tenderType',
    'eventAmt',
    'remainingAmt',
    'details',
    'camera',
  ],
  other_discount: [
    'date',
    'time',
    'dayPart',
    'register',
    'crew',
    'manager',
    'mgr_code',
    'tenderType',
    'eventAmt',
    'remainingAmt',
    'details',
    'camera',
  ],
  loyalty_reward_ids: [
    'date',
    'time',
    'loyaltyId',
    'dayPart',
    'register',
    'crew',
    'eventType',
    'eventAmt',
    'details',
    'camera',
  ],
  drawer_open: ['date', 'time', 'dayPart', 'register', 'crew', 'camera'],
  general_drawer_open: ['date', 'time', 'register', 'manager', 'camera'],
  duplicate_card_swipe: [
    'date',
    'time',
    'dayPart',
    'register',
    'crew',
    'manager',
    'mgr_code',
    'eventAmt',
    'details',
    'camera',
  ],
  all_promo: [
    'date',
    'time',
    'dayPart',
    'register',
    'crew',
    'manager',
    'mgr_code',
    'tenderType',
    'promoType',
    'eventAmt',
    'remainingAmt',
    'details',
    'camera',
  ],
  all_discount: [
    'date',
    'time',
    'dayPart',
    'register',
    'crew',
    'manager',
    'mgr_code',
    'tenderType',
    'discountType',
    'eventAmt',
    'remainingAmt',
    'details',
    'camera',
  ],
  billable_sale: [
    'date',
    'time',
    'dayPart',
    'register',
    'crew',
    'manager',
    'mgr_code',
    // 'org',
    'tenderType',
    'eventAmt',
    'details',
    'camera',
  ],
  high_lock_out: [
    'date',
    'time',
    'dayPart',
    'register',
    'crew',
    'manager',
    'mgr_code',
    'tenderType',
    'lock_out_type',
    'item_count',
    'eventAmt',
    'details',
    'camera',
  ],
  coupon: [
    'date',
    'time',
    'dayPart',
    'register',
    'crew',
    'manager',
    'mgr_code',
    'tenderType',
    'couponType',
    'eventAmt',
    'remainingAmt',
    'details',
    'camera',
  ],
  electronic_benefit_transfer: [
    'date',
    'time',
    'dayPart',
    'register',
    'crew',
    'manager',
    'mgr_code',
    // 'org',
    'tenderType',
    'eventAmt',
    'details',
    'camera',
  ],
  default: [
    'date',
    'time',
    'dayPart',
    'register',
    'crew',
    'manager',
    'mgr_code',
    'tenderType',
    'eventAmt',
    'details',
    'camera',
  ],
};